import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { getTeamLogoUrl, getMedalImageUrl } from '../../utils/formatters';
import ImageLightbox from '../common/ImageLightbox';
import '../leagues/Leagues.css';

// Placement labels translated; keys resolved via t() at render (module-level
// constant can't call hooks).
const PLACEMENT_LABEL = { 1: 'leagues.placeGold', 2: 'leagues.placeSilver', 3: 'leagues.placeBronze' };
const TIER_CLASS = { 1: 'gold', 2: 'silver', 3: 'bronze' };

/**
 * Player-profile section listing the leagues a player takes part in — both
 * individual (duel) participations and team-based ones (seasons where the player
 * is on a competing roster) — plus any league champion medals they've won.
 * Renders nothing when the player is in no leagues and has no medals, unless
 * `showEmpty` is set (used by the profile's dedicated leagues tab, which must
 * never be blank) — then an empty state with a link to the leagues hub shows.
 *
 * @param {object} props - Component props.
 * @param {string} props.steamid64 - The profile owner's Steam ID.
 * @param {boolean} [props.showEmpty=false] - Render an empty state instead of null.
 */
export default function LeaguesProfileSection({ steamid64, showEmpty = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [medals, setMedals] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    Promise.all([
      fetch(`${API_URL}/api/leagues/player/${steamid64}`).then((r) => (r.ok ? r.json() : [])),
      fetch(`${API_URL}/api/leagues/medals/player/${steamid64}`).then((r) => (r.ok ? r.json() : [])),
    ])
      .then(([leagueRows, medalRows]) => {
        if (!active) return;
        setRows(Array.isArray(leagueRows) ? leagueRows : []);
        setMedals(Array.isArray(medalRows) ? medalRows : []);
      })
      .catch(() => {
        if (!active) return;
        setRows([]);
        setMedals([]);
      })
      .finally(() => active && setLoaded(true));
    return () => { active = false; };
  }, [steamid64]);

  if (!loaded) return null;
  if (rows.length === 0 && medals.length === 0) {
    if (!showEmpty) return null;
    return (
      <div className="stats-section">
        <h2>{t('nav.leagues')}</h2>
        <p className="profile-leagues-empty">{t('leagues.noLeagues')}</p>
        <button className="profile-leagues-empty-link" onClick={() => navigate('/leagues')}>
          {t('nav.leagues')} →
        </button>
      </div>
    );
  }

  const open = (r) => (r.team ? navigate(`/leagues/teams/${r.team.id}`) : navigate(`/leagues/${r.league.id}`));

  return (
    <div className="stats-section">
      <h2>{t('nav.leagues')}</h2>
      {medals.length > 0 && (
        <div className="league-medal-grid">
          {medals.map((m) => {
            const tier = TIER_CLASS[m.placement] || '';
            return (
              <div
                key={m.id}
                className={`league-medal-card ${tier} clickable`}
                onClick={() => setLightbox(m)}
              >
                {m.image_filename && (
                  <img
                    src={getMedalImageUrl(m.image_filename)}
                    alt={`${m.league?.name || ''} ${PLACEMENT_LABEL[m.placement] ? t(PLACEMENT_LABEL[m.placement]) : ''}`}
                    className="league-medal-card-img"
                  />
                )}
                <div className="league-medal-card-info">
                  <span className="league-medal-card-league">{m.league?.name}</span>
                  <span className="league-medal-card-season">{m.season?.name}</span>
                  <span className={`league-medal-card-rank ${tier || 'other'}`}>
                    {PLACEMENT_LABEL[m.placement] ? t(PLACEMENT_LABEL[m.placement]) : `#${m.placement}`}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {rows.length > 0 && (
      <div className="profile-leagues">
        {rows.map((r) => {
          const logo = r.team ? getTeamLogoUrl(r.team.logo_url) : null;
          return (
            <button key={r.id} className="profile-league-row" onClick={() => open(r)}>
              {r.team ? (
                logo
                  ? <img className="team-logo" src={logo} alt="" />
                  : <span className="team-logo team-logo-blank">{(r.team.tag || r.team.name || '?')[0]}</span>
              ) : (
                <span className="league-mode-badge">{r.league.game_mode_display}</span>
              )}
              <div className="profile-league-main">
                <span className="profile-league-name">{r.league.name}</span>
                <span className="profile-league-sub">
                  {r.season.name}{r.team ? ` · ${r.display_name}` : ''}
                </span>
              </div>
              <div className="profile-league-stat">
                <span className="profile-league-rank">{r.rank ? `#${r.rank}` : '–'}</span>
                <span className="profile-league-pts">{t('leagues.points', { count: r.points })}</span>
              </div>
            </button>
          );
        })}
      </div>
      )}
      {lightbox && (
        <ImageLightbox
          src={getMedalImageUrl(lightbox.image_filename)}
          title={t('tournament.medalLightboxTitle', {
            name: lightbox.league?.name || '',
            place: PLACEMENT_LABEL[lightbox.placement] ? t(PLACEMENT_LABEL[lightbox.placement]) : `#${lightbox.placement}`,
          })}
          subtitle={lightbox.season?.name || ''}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
